import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  Button,
  Box,
  Collapse,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useState } from "react";
import { DestinationViewType } from "generated-graphql/graphql";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import {
  MergedWasteStreamDestinationsQuery,
  MergedWasteStreamDestinationsQueryVariables,
} from "generated-graphql/graphql";
import { gql } from "generated-graphql/gql";
import { useDestinationsGrid } from "../../WasteStreamDetails/Destinations/useDestinationsGrid";

const MERGED_WASTE_STREAM_DESTINATIONS = gql(`
  query MergedWasteStreamDestinations(
    $wasteStreamIds: [ID!]!
    $viewType: DestinationViewType!
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
  ) {
    mergedWasteStreamDestinations(
      wasteStreamIds: $wasteStreamIds
      viewType: $viewType
      search: $search
      page: $page
      pageSize: $pageSize
      sort: $sort
    ) {
      items {
        destinationFacilityId
        destinationFacilityName
        managementMethodCode
        vendorAmount
        eManifestAmount
      }
      count
    }
  }
`);

interface MergedDestinationsDataGridProps {
  wasteStreamIds: string[];
}

const viewOptions = [
  { value: DestinationViewType.Destinations, label: "Destinations" },
  { value: DestinationViewType.ManagementMethods, label: "Management Methods" },
  {
    value: DestinationViewType.DestinationsByManagementMethod,
    label: "Destinations by Management Method",
  },
];

export const MergedDestinationsDataGrid = ({
  wasteStreamIds,
}: MergedDestinationsDataGridProps) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [viewType, setViewType] = useState<DestinationViewType>(
    DestinationViewType.Destinations
  );

  const { columns, getRowId } = useDestinationsGrid(viewType);

  return (
    <>
      <Stack direction="row" alignItems="center" sx={{ my: 3 }}>
        {/* Horizontal rule behind the button */}
        <Button
          onClick={() => setExpanded(!expanded)}
          endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{
            padding: theme.spacing(1, 2),
            backgroundColor: theme.palette.background.paper,
            position: "relative",
            zIndex: 1,
            pt: 0,
            pb: 0,
            pl: 0,
          }}
        >
          {expanded ? "HIDE" : "SHOW"} DESTINATIONS / MANAGEMENT METHODS
        </Button>
        <Box
          sx={{
            height: "1px",
            flex: 1,
            backgroundColor: theme.palette.grey[300],
          }}
        />
      </Stack>
      <Collapse in={expanded}>
        <Stack>
          <Stack
            direction="row"
            alignItems="start"
            justifyContent="space-between"
          >
            <Typography>Merged Destinations</Typography>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>View by</InputLabel>
              <Select
                value={viewType}
                onChange={(e) =>
                  setViewType(e.target.value as DestinationViewType)
                }
                label="View by"
                size="small"
              >
                {viewOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <OmnisearchDataGrid<
            MergedWasteStreamDestinationsQuery,
            MergedWasteStreamDestinationsQueryVariables,
            any
          >
            withPadding={false}
            persistenceKey="merged-waste-stream-destinations"
            isURLDriven={false}
            columns={columns}
            dataQuery={MERGED_WASTE_STREAM_DESTINATIONS}
            getRowId={getRowId}
            getItems={(data) => data.mergedWasteStreamDestinations?.items ?? []}
            getCount={(data) => data.mergedWasteStreamDestinations?.count ?? 0}
            dataQueryVariables={{
              wasteStreamIds,
              viewType,
            }}
            noDataMessage="No destinations found for the selected waste streams."
            hideOmnisearch={true}
            showFilters={false}
            showManageColumns={false}
          />
        </Stack>
      </Collapse>
    </>
  );
};
